/**
 * Quiz Arm B — Starter Stak Deluxe (SSD) hero binding.
 *
 * Reuses the existing shade-selection cascade rather than building a new matcher:
 *   1. Kustomer shade-match worker (violet-shade-match.js) — already auto-selects
 *      shades site-wide on elements with [data-option-value]; if it sets a pill, we keep it.
 *   2. Quiz mapping — the `stak` object the quiz already produced (cookie `post`),
 *      whose stak.bb_powder / stak.concealer ARE option1 / option2 on the SSD.
 *   3. Default — first available variant.
 *
 * The SSD has two options: option1 = BB Powder shade, option2 = Cream Concealer shade.
 * Powder Bronzer is derived from the shade depth (locked, display only). This script
 * pre-selects the matched variant, swaps the hero image, fills the read-only chips,
 * and wires "Add your stak to bag".
 *
 * `retrieveStak` / `getCookie` are globals defined by quiz.js (loaded before this file).
 */
(function () {
  var section = document.querySelector('.quiz-result-ssd');
  if (!section) return;

  var dataEl = section.querySelector('[data-ssd-variants]');
  var variants = [];
  try { variants = JSON.parse(dataEl.textContent.trim()); } catch (e) { return; }
  if (!variants.length) return;

  var idInput = section.querySelector('#ssd-variant-id');
  var heroImg = section.querySelector('#ssd-hero-image');
  var bronzerEl = section.querySelector('[data-ssd-bronzer]');
  var addBtn = section.querySelector('#ssd-add-to-bag');
  var notifyEl = section.querySelector('#ssd-notify');
  var selectors = {
    1: section.querySelector('[data-ssd-selector="1"]'), // BB Powder
    2: section.querySelector('[data-ssd-selector="2"]')  // Cream Concealer
  };

  // The SSD's Powder Bronzer is derived from the matched CONCEALER shade (option2),
  // via the shared map in snippets/bronzer-shade-map.liquid that the Stak PDP uses too
  // — this used to guess from the BB Powder family, which disagreed with the PDP and
  // could never produce Leche. Falls back to the old generic copy on an unmapped shade
  // rather than naming the wrong bronzer.
  var bronzerMap = {};
  var bronzerMapEl = section.querySelector('[data-ssd-bronzer-map]');
  if (bronzerMapEl) {
    bronzerMapEl.textContent.trim().split('|').forEach(function (pair) {
      var parts = pair.split(':');
      if (parts[0] && parts[1]) bronzerMap[parts[0]] = parts[1];
    });
  }

  function bronzerForConcealer(concealer) {
    return bronzerMap[concealer] || '';
  }

  // BB Powder ("1") + Cream Concealer ("2") swatch images, keyed by shade name ->
  // Files image URL (same source as the SSD PDP), or null when no file exists.
  var shadeImages = {};
  var imgDataEl = section.querySelector('[data-ssd-shade-images]');
  if (imgDataEl) {
    try { shadeImages = JSON.parse(imgDataEl.textContent.trim()); } catch (e) { shadeImages = {}; }
  }
  var panEls = {
    1: section.querySelector('[data-ssd-pan="1"]'),
    2: section.querySelector('[data-ssd-pan="2"]'),
    3: section.querySelector('[data-ssd-pan="3"]')  // bronzer, keyed by concealer
  };

  // Point a pan <img> (and the matching dropdown dot) at the swatch image for a
  // shade; fall back to the neutral style when no Files image exists.
  function setPanImage(key, shadeMapKey, shade, dotEl) {
    var url = shadeImages[shadeMapKey] ? shadeImages[shadeMapKey][shade] : null;
    var pan = panEls[key];
    if (pan) {
      if (url) {
        pan.setAttribute('src', url);
        pan.classList.remove('ssd-item__pan--fallback');
      } else {
        pan.removeAttribute('src');
        pan.classList.add('ssd-item__pan--fallback');
      }
    }
    if (dotEl) {
      dotEl.style.backgroundImage = url ? 'url(' + url + ')' : '';
    }
  }

  // ── Lookups ────────────────────────────────────────────────────────────────
  var byCombo = {};            // "o1|o2" -> variant
  var o1Available = {};        // o1 -> true if any available variant
  var o2Available = {};        // o2 -> true if any available variant
  var imageByO1 = {};          // o1 -> image src
  variants.forEach(function (v) {
    byCombo[v.o1 + '|' + v.o2] = v;
    if (v.a) { o1Available[v.o1] = true; o2Available[v.o2] = true; }
    if (!imageByO1[v.o1] && v.img) imageByO1[v.o1] = v.img;
  });

  var current = { 1: null, 2: null };
  // The shade-matched value per option (initial cascade + any worker re-match). A shade
  // equal to this is still "matched"; only a user pick that differs is "your pick".
  var matched = { 1: null, 2: null };

  function firstAvailable() {
    for (var i = 0; i < variants.length; i++) { if (variants[i].a) return variants[i]; }
    return variants[0];
  }

  // Resolve a variant for the desired (o1, o2). If that exact combo is missing or
  // unavailable, keep `fixed` option and pick the first available partner for it.
  function resolveVariant(o1, o2, fixed) {
    var exact = byCombo[o1 + '|' + o2];
    if (exact && exact.a) return exact;

    if (fixed === 1) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].o1 === o1 && variants[i].a) return variants[i];
      }
    } else if (fixed === 2) {
      for (var j = 0; j < variants.length; j++) {
        if (variants[j].o2 === o2 && variants[j].a) return variants[j];
      }
    }
    return exact || firstAvailable();
  }

  // ── Apply a resolved variant to the UI ──────────────────────────────────────
  function applyVariant(v) {
    if (!v) return;
    current[1] = v.o1;
    current[2] = v.o2;

    if (idInput) idInput.value = v.id;

    var src = v.img || imageByO1[v.o1];
    if (heroImg && src) heroImg.src = src;

    setPill(1, v.o1);
    setPill(2, v.o2);

    // Swap the BB Powder + Cream Concealer swatch/pan images to match the selected
    // shades (PDP parity).
    setPanImage(1, '1', v.o1, dotFor(1));
    setPanImage(2, '2', v.o2, dotFor(2));

    // Powder Bronzer: show the recommended (locked) shade for this concealer.
    // Its swatch is keyed by the concealer too, so it rides the same map.
    setPanImage(3, '3', v.o2, null);
    if (bronzerEl) {
      var bronzer = bronzerForConcealer(v.o2);
      var bronzerChip = bronzerEl.closest('.ssd-chip');
      if (panEls[3] && bronzer) panEls[3].setAttribute('alt', bronzer);
      if (bronzer) {
        bronzerEl.textContent = bronzer;
        if (bronzerChip) bronzerChip.style.display = '';
      } else if (bronzerChip) {
        // Unmapped concealer — hide the chip so the row falls back to its
        // "matched to your concealer" caption instead of naming a wrong shade.
        bronzerChip.style.display = 'none';
      }
    }

    // Out-of-stock shade -> swap the CTA to "Notify Me" (Klaviyo BIS), like the PDP.
    var soldOut = v.a === false;
    if (addBtn) addBtn.style.display = soldOut ? 'none' : '';
    if (notifyEl) notifyEl.style.display = soldOut ? 'flex' : 'none';
  }

  function dotFor(position) {
    var wrap = selectors[position];
    return wrap ? wrap.querySelector('[data-ssd-dot]') : null;
  }

  function setPill(position, value) {
    var wrap = selectors[position];
    if (!wrap) return;
    var label = wrap.querySelector('[data-ssd-current]');
    if (label) label.textContent = value;
    wrap.querySelectorAll('.ssd-shade-option').forEach(function (btn) {
      btn.classList.toggle('selected', btn.getAttribute('data-option-value') === value);
    });
  }

  // ── Initial shade resolution (cascade) ──────────────────────────────────────
  function getStak() {
    try {
      if (typeof retrieveStak === 'function' && typeof getCookie === 'function') {
        var raw = getCookie('post');
        if (raw) return retrieveStak(raw) || null;
      }
    } catch (e) {}
    return null;
  }

  function initialVariant() {
    var stak = getStak();
    var bb = stak && stak.bb_powder;
    var cn = stak && stak.concealer;

    // 1 + 2: quiz mapping (already reflects the quiz/selfie match). Validate against
    // real option values; fall back to first available if absent/unknown.
    if (bb && cn && byCombo[bb + '|' + cn]) return byCombo[bb + '|' + cn];
    if (bb && o1Available[bb]) return resolveVariant(bb, cn, 1);
    if (cn && o2Available[cn]) return resolveVariant(bb, cn, 2);
    return firstAvailable();
  }

  // ── Interaction: pill toggle + option select ────────────────────────────────
  function closeAllPanels(except) {
    section.querySelectorAll('[data-ssd-panel]').forEach(function (p) {
      if (p !== except) {
        p.hidden = true;
        var pill = p.parentElement.querySelector('[data-ssd-pill]');
        if (pill) pill.setAttribute('aria-expanded', 'false');
      }
    });
  }

  Object.keys(selectors).forEach(function (pos) {
    var wrap = selectors[pos];
    if (!wrap) return;
    var pill = wrap.querySelector('[data-ssd-pill]');
    var panel = wrap.querySelector('[data-ssd-panel]');

    if (pill && panel) {
      pill.addEventListener('click', function (e) {
        e.preventDefault();
        var willOpen = panel.hidden;
        closeAllPanels(willOpen ? panel : null);
        panel.hidden = !willOpen;
        pill.setAttribute('aria-expanded', String(willOpen));
      });
    }

    wrap.querySelectorAll('.ssd-shade-option').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var value = btn.getAttribute('data-option-value');
        var position = parseInt(pos, 10);
        var o1 = position === 1 ? value : current[1];
        var o2 = position === 2 ? value : current[2];
        // Keep an out-of-stock shade selected (it becomes a "Notify Me", like the PDP).
        // Only fall back to a nearby available variant when the exact combo isn't offered.
        var exact = byCombo[o1 + '|' + o2];
        applyVariant(exact || resolveVariant(o1, o2, position));
        if (panel) panel.hidden = true;
        if (pill) pill.setAttribute('aria-expanded', 'false');

        var hint = wrap.querySelector('[data-ssd-hint]');
        if (!e.isTrusted) {
          // Programmatic selection = the shade-match worker. This IS a match — keep the
          // "matched" label and update the matched baseline to the new recommended shade.
          matched[position] = value;
          if (hint) hint.textContent = 'matched · tap to change';
        } else {
          // Real shopper pick: only "your pick" when it differs from the matched shade.
          if (hint) hint.textContent = (value === matched[position])
            ? 'matched · tap to change'
            : 'your pick · tap to change';
        }
      });
    });
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-ssd-selector]')) closeAllPanels(null);
  });

  // ── Add to bag ──────────────────────────────────────────────────────────────
  if (addBtn) {
    addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = idInput && idInput.value;
      if (!variantId) return;

      addBtn.classList.add('is-loading');
      if (typeof gtag === 'function') {
        gtag('event', 'click', { event_category: 'quiz', event_label: 'add ssd stak to bag' });
      }

      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function () {
          addBtn.classList.remove('is-loading');
          if (typeof window.quizQuickCartRefreshOpen === 'function') {
            window.quizQuickCartRefreshOpen();
          } else {
            window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
          }
        })
        .catch(function () { addBtn.classList.remove('is-loading'); });
    });
  }

  // ── Bind now, and re-bind when results are revealed ─────────────────────────
  function bind() {
    var v = initialVariant();
    applyVariant(v);
    if (v) { matched[1] = v.o1; matched[2] = v.o2; }
  }
  bind();

  ['a[href="#see-result"]', 'a[href="#see-my-results"]'].forEach(function (sel) {
    var el = document.querySelector(sel);
    if (el) el.addEventListener('click', function () { setTimeout(bind, 50); });
  });
})();
